import { FetchResult, MutationResult, useMutation } from '@apollo/client';

import { CreateProductMutation, CreateProductMutationVariables, CREATE_PRODUCT, GET_PRODUCTS } from '../graphql';
import { useCurrentCompanyId } from './useCurrentCompanyId';
import { useProducts } from './useProducts';

export const useCreateProduct = (): [
  (
    label: string,
    { ascProjectId, rdProjectId }: { ascProjectId?: string; rdProjectId?: string },
  ) => Promise<FetchResult<CreateProductMutation>>,
  MutationResult<CreateProductMutation>,
] => {
  const companyId = useCurrentCompanyId();
  const { products } = useProducts();
  const [createProduct, result] = useMutation<CreateProductMutation, CreateProductMutationVariables>(CREATE_PRODUCT);

  const handleCreateProduct = (
    label: string,
    { ascProjectId, rdProjectId }: { ascProjectId?: string; rdProjectId?: string },
  ): Promise<FetchResult<CreateProductMutation>> =>
    createProduct({
      variables: { companyId, createProductInput: { name: label, ascProjectId, rdProjectId } },
      optimisticResponse: {
        __typename: 'Mutation',
        createProduct: {
          __typename: 'Product',
          id: label,
          name: label,
        },
      },
      update: (cache, { data }) => {
        if (data?.createProduct) {
          cache.writeQuery({
            query: GET_PRODUCTS,
            data: {
              getProducts: [...products, data.createProduct],
            },
            variables: {
              companyId,
            },
            broadcast: true,
          });
          if (ascProjectId) {
            cache.modify({
              id: `AscProject:${ascProjectId}`,
              fields: {
                product: () => data.createProduct,
              },
              broadcast: true,
            });
          }
          if (rdProjectId) {
            cache.modify({
              id: `RdProject:${rdProjectId}`,
              fields: {
                product: () => data.createProduct,
              },
              broadcast: true,
            });
          }
        }
      },
    });

  return [handleCreateProduct, result];
};
